import { Ring } from '@uiball/loaders'
import React, { useMemo, useState } from 'react'
import { BrowserView, isMobile, MobileView } from 'react-device-detect'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import { Button } from '../../components/Button'
import { CustomText } from '../../components/CustomText'
import { Container, Content as ExContent } from '../../components/styled-components'
import Colors from '../../utils/Colors'
import { useContentDetails } from '../../hooks/useContentDetails'
import { BackButton } from '../../components/BackButton'
import { useContentFunctions } from '../../hooks/useContentFunctions'
import moment from 'moment/moment'
import { Loader } from '../../components/Loader'
import { MediaResponseDto, MediaTypeEnum, TaskRecurrenceEnum } from '../../new-api/api'
import { TaskMediaModal } from '../../components/TaskMediaModal'
import { BASE_URL } from '../../utils/shared'
import fileTextSrc from '../../assets/icons/FileText.svg'
import pdfIconSrc from '../../assets/icons/file-icons/pdf-icon.svg'
import wordIconSrc from '../../assets/icons/file-icons/word-icon.svg'
import excelIconSrc from '../../assets/icons/file-icons/excel-icon.svg'
import pptIconSrc from '../../assets/icons/file-icons/pptx-icon.svg'

function normalizeExt(ext?: string) {
  return ext?.toLowerCase().replace(/^\./, '') ?? ''
}

function getDocBg(ext: string): string {
  if (ext === 'pdf') return '#FEF2F2'
  if (['xls', 'xlsx'].includes(ext)) return '#F0FDF4'
  if (['doc', 'docx'].includes(ext)) return '#EFF6FF'
  if (['ppt', 'pptx'].includes(ext)) return '#B6462729'
  return '#F5F5F5'
}

function getDocIcon(ext: string): string {
  switch (ext) {
    case 'pdf':
      return pdfIconSrc
    case 'doc':
    case 'docx':
      return wordIconSrc
    case 'xls':
    case 'xlsx':
      return excelIconSrc
    case 'ppt':
    case 'pptx':
      return pptIconSrc
    default:
      return fileTextSrc
  }
}

const Content = styled(ExContent)<{ isLoading: boolean }>`
  display: flex;
  justify-content: ${({ isLoading }) => (isLoading ? 'center' : 'flex-start')};
  flex-direction: column;
  border-radius: 6px;
  background-color: white;
  margin-top: 50px;
  padding-top: 40px;
  padding-bottom: 40px;
`

const InnerContent = styled.div`
  margin-left: 36px;
  margin-right: 36px;
`

const UpperContent = styled.div`
  padding-top: ${isMobile ? 20 : 0}px;
  padding-left: ${isMobile ? 20 : 0}px;
  padding-right: ${isMobile ? 20 : 0}px;
`

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 20px;
  column-gap: 16px;
  ${isMobile && 'align-items: center; flex-direction: column;'}
`

const MediaSection = styled.div`
  margin-top: 24px;
  padding: 0 ${isMobile ? 20 : 0}px;
`

const MediaThumbRow = styled.div`
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding-bottom: 4px;
  &::-webkit-scrollbar {
    height: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #ddd;
    border-radius: 2px;
  }
`

const MediaThumb = styled.div`
  width: 90px;
  height: 90px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
  cursor: pointer;
  background: #e7eaec;
  border: 1px solid #dedede;
  &:hover {
    border-color: #59eeb9;
  }
  transition: border-color 0.15s;
`

const ImgThumb = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const VidThumbWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`

const VidPlayOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.35);
  color: #fff;
  font-size: 18px;
`

const DocThumb = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
`

const DocThumbExt = styled.span`
  font-size: 9px;
  font-weight: 700;
  color: #555;
  text-transform: uppercase;
  line-height: 1;
`

const CommentsSection = styled.div`
  margin-top: 24px;
  border-top: 1px solid ${Colors.black10};
  padding-top: 20px;
`

function mediaUrl(url: string) {
  if (url?.startsWith('http')) return url
  return `${BASE_URL}/files/${url}`
}

export const TaskPage = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const { data: content, isLoading } = useContentDetails({
    contentId: Number(id),
  })
  const contentInteractionWithFinishedAt = (content?.contentInteractions || []).find((ci) => !!ci.finishedAt)
  const [modalIndex, setModalIndex] = useState<number | null>(null)

  const {
    markContentFinished: { mutateAsync: markContentFinished, isLoading: isMarkingContentFinished },
  } = useContentFunctions({ contentId: Number(id) })

  const { t } = useTranslation()
  const taskMedias = (content?.taskMedias || []) as MediaResponseDto[]

  const innerContent = useMemo(
    () => (
      <InnerContent>
        <UpperContent>
          <CustomText fontSize={13} lineHeight={1.4} uppercase color={Colors.gray} style={{ marginBottom: 10 }}>
            {t(content?.type) || ''}
          </CustomText>
          <CustomText
            fontSize={20}
            lineHeight={1}
            fontWeight={500}
            color={Colors.darkGray}
            style={{ marginBottom: 10 }}
          >
            {content?.name || ''}
          </CustomText>

          <CustomText fontSize={14} lineHeight={1.4} color={Colors.darkGray} style={{ marginBottom: 12 }}>
            {content?.description || ''}
          </CustomText>
          <CustomText fontSize={14} lineHeight={1.4} uppercase color={Colors.gray}>
            {t('taskBy')}
          </CustomText>
          <CustomText fontSize={16} lineHeight={1.4} fontWeight={500} style={{ marginBottom: 20 }}>
            {content?.creator?.name}
          </CustomText>
          <CustomText fontSize={14} lineHeight={1.4} uppercase color={Colors.gray}>
            {t(contentInteractionWithFinishedAt ? 'finished' : 'deadline')}
          </CustomText>
          <CustomText fontSize={16} lineHeight={1.4} fontWeight={500} style={{ marginBottom: 20 }}>
            {moment(
              contentInteractionWithFinishedAt ? contentInteractionWithFinishedAt.finishedAt : content?.task?.deadline,
            ).format('DD.MM.YYYY., HH:mm')}
          </CustomText>
          {content?.task?.recurrence && content.task.recurrence !== TaskRecurrenceEnum.NO_REPEAT && (
            <>
              <CustomText fontSize={14} lineHeight={1.4} uppercase color={Colors.gray}>
                {t('recurrence')}
              </CustomText>
              <CustomText fontSize={16} lineHeight={1.4} fontWeight={500} style={{ marginBottom: 20 }}>
                {t(content.task.recurrence.toLowerCase())}
              </CustomText>
            </>
          )}
        </UpperContent>

        {taskMedias.length > 0 && (
          <MediaSection>
            <CustomText fontSize={13} lineHeight={1.4} uppercase color={Colors.gray} style={{ marginBottom: 10 }}>
              {t('attachedContent')} ({taskMedias.length})
            </CustomText>
            <MediaThumbRow>
              {taskMedias.map((m, idx) => {
                const url = mediaUrl(m.url)
                const vid = m.type === MediaTypeEnum.VIDEO
                const doc = m.type === MediaTypeEnum.DOCUMENT
                return (
                  <MediaThumb key={idx} onClick={() => setModalIndex(idx)}>
                    {doc ? (
                      <DocThumb style={{ backgroundColor: getDocBg(normalizeExt(m.extension)) }}>
                        <img
                          src={getDocIcon(normalizeExt(m.extension))}
                          alt={normalizeExt(m.extension)}
                          style={{ width: 32, height: 32, opacity: 0.85 }}
                        />
                        <DocThumbExt>{normalizeExt(m.extension).toUpperCase()}</DocThumbExt>
                      </DocThumb>
                    ) : vid ? (
                      <VidThumbWrapper>
                        <video
                          src={url}
                          muted
                          preload="metadata"
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          crossOrigin="use-credentials"
                        />
                        <VidPlayOverlay>▶</VidPlayOverlay>
                      </VidThumbWrapper>
                    ) : (
                      <ImgThumb src={url} alt={m.name} crossOrigin="use-credentials" />
                    )}
                  </MediaThumb>
                )
              })}
            </MediaThumbRow>
          </MediaSection>
        )}

        {content?.settings?.comments && (content?.comments || []).length > 0 && (
          <CommentsSection>
            <CustomText
              fontSize={18}
              lineHeight={1}
              fontWeight={500}
              color={Colors.darkGray}
              style={{ marginBottom: 12 }}
            >
              {t('myComments')}
            </CustomText>
            {(content.comments || []).map((c: any) => (
              <div key={c.id} style={{ marginBottom: 8 }}>
                <CustomText fontSize={14} lineHeight={1.4}>
                  {c?.title || ''}
                </CustomText>
                <CustomText fontSize={12} color={Colors.gray}>
                  {moment(c?.createdAt).format('DD.MM.YYYY.')}
                </CustomText>
              </div>
            ))}
          </CommentsSection>
        )}

        <ButtonContainer>
          <BackButton />
          {content?.settings?.comments && !contentInteractionWithFinishedAt && (
            <Button
              loading={false}
              text={t('sendComment')}
              onClick={() => navigate(`/content/${id}/comment`)}
              buttonType="secondary"
            />
          )}
          {contentInteractionWithFinishedAt ? (
            content?.settings?.answerEnabled && (
              <Button
                loading={false}
                text={t('viewAnswer')}
                onClick={() => navigate(`/task/${id}/answer`)}
                buttonType="primary"
              />
            )
          ) : content?.settings?.answerEnabled ? (
            <Button
              loading={false}
              text={t('yourAnswer')}
              onClick={() => navigate(`/task/${id}/answer/submit`)}
              buttonType="primary"
            />
          ) : (
            <Button
              loading={isMarkingContentFinished}
              text={t('markAsDone')}
              onClick={async () => {
                await markContentFinished()
                navigate(-1)
              }}
              buttonType="primary"
            />
          )}
        </ButtonContainer>
      </InnerContent>
    ),
    [
      content?.creator?.name,
      content?.description,
      content?.name,
      content?.task?.deadline,
      content?.type,
      content?.settings?.answerEnabled,
      content?.settings?.comments,
      content?.comments,
      contentInteractionWithFinishedAt,
      taskMedias,
      id,
      isMarkingContentFinished,
      markContentFinished,
      navigate,
      setModalIndex,
      t,
    ],
  )

  return (
    <>
      {modalIndex !== null && (
        <TaskMediaModal medias={taskMedias} initialIndex={modalIndex} onClose={() => setModalIndex(null)} />
      )}
      <Container style={{ backgroundColor: isMobile ? Colors.white : Colors.background }}>
        <BrowserView renderWithFragment>
          <Content isLoading={isLoading}>{isLoading || !content ? <Loader /> : innerContent}</Content>
        </BrowserView>
        <MobileView>{isLoading || !content ? <Ring color={Colors.primaryGreen} /> : innerContent}</MobileView>
      </Container>
    </>
  )
}
